import type { QueueItem, QueueViewSpec } from '../model.js';

export const queueGuidedTargets = {
  head: 'queue.item.chk-8841',
  doomed: 'queue.group.doomed',
  deadline: 'queue.deadline',
  capacity: 'queue.capacity',
  rejected: 'queue.rejected',
} as const;

/**
 * A PostgreSQL connection pool under sustained overload. The pool serves 200
 * checkouts per second while 500 arrive, so the queue is saturated and its
 * head has been waiting more than twice the client timeout.
 */
export function queueViewFixture(
  width = 640,
  stress = false,
  focus: QueueViewSpec['focus'] = 'head',
): QueueViewSpec {
  return {
    id: stress ? 'queue-view-stress' : 'queue-view',
    family: 'queue-view',
    view: 'plot',
    viewport: { width, height: stress ? (width >= 560 ? 700 : 780) : width >= 560 ? 520 : 660 },
    capacity: 8,
    arrivalsPerSecond: stress ? 1200 : 500,
    servicePerSecond: stress ? 1 : 2,
    clientTimeoutMs: stress ? 2500 : 2000,
    items: stress ? stressItems() : normalItems(),
    focus,
    provenance: stress
      ? 'pgbouncer checkout queue · eight waiters in front of a pool clearing one checkout per second, long request paths preserved verbatim'
      : 'PostgreSQL checkout queue · eight waiters in front of a four-connection pool clearing two checkouts per second, sampled at one instant',
  };
}

/** A pool that is still absorbing the burst: free slots remain, the head is already late. */
export function queueViewPartialFixture(width = 640): QueueViewSpec {
  return {
    id: 'queue-view-partial',
    family: 'queue-view',
    view: 'plot',
    viewport: { width, height: width >= 560 ? 520 : 660 },
    capacity: 8,
    arrivalsPerSecond: 5,
    servicePerSecond: 2,
    clientTimeoutMs: 1500,
    items: partialItems(),
    focus: 'head',
    provenance: 'PostgreSQL connection-pool checkout queue · burst absorbed, three slots still free',
  };
}

function partialItems(): QueueItem[] {
  return [
    { id: 'chk-8841', label: 'GET /orders/8841', waitedMs: 2410 },
    { id: 'chk-8843', label: 'POST /checkout', waitedMs: 1880 },
    { id: 'chk-8846', label: 'GET /cart/2201', waitedMs: 1355 },
    { id: 'chk-8850', label: 'POST /payments', waitedMs: 780 },
    { id: 'chk-8852', label: 'GET /orders/8852', waitedMs: 240 },
  ];
}

function normalItems(): QueueItem[] {
  return [
    { id: 'chk-8841', label: 'GET /orders/8841', waitedMs: 4452 },
    { id: 'chk-8843', label: 'POST /checkout', waitedMs: 3810 },
    { id: 'chk-8846', label: 'GET /cart/2201', waitedMs: 3125 },
    { id: 'chk-8850', label: 'POST /payments', waitedMs: 2480 },
    { id: 'chk-8852', label: 'GET /orders/8852', waitedMs: 2065 },
    { id: 'chk-8857', label: 'GET /inventory', waitedMs: 1290 },
    { id: 'chk-8861', label: 'POST /refunds', waitedMs: 640 },
    { id: 'chk-8864', label: 'GET /cart/2209', waitedMs: 120 },
  ];
}

function stressItems(): QueueItem[] {
  return [
    { id: 'chk-201841', label: 'POST /v1/checkout/session', waitedMs: 9840 },
    { id: 'chk-201847', label: 'GET /v1/orders/8841/lines', waitedMs: 8615 },
    { id: 'chk-201852', label: 'POST /v1/payments/capture', waitedMs: 7320 },
    { id: 'chk-201858', label: 'GET /v1/inventory/eu-west-2', waitedMs: 6140 },
    { id: 'chk-201863', label: 'POST /v1/refunds/partial', waitedMs: 4905 },
    { id: 'chk-201869', label: 'GET /v1/customers/184467', waitedMs: 3260 },
    { id: 'chk-201874', label: 'POST /v1/carts/2209/items', waitedMs: 1580 },
    { id: 'chk-201881', label: 'GET /v1/orders/8852/status', waitedMs: 240 },
  ];
}

/* ------------------------------------------------------------------------ *
 * Teaching truth: one saturated pool, three moments, and two quantities the
 * learner may change. Every age below is derived from the queue itself —
 * arrival spacing while the pool was filling, then depth ÷ service rate once
 * the queue is standing full. Nothing here is decorative.
 * ------------------------------------------------------------------------ */

/** Semantic objects a learner is allowed to select in this family. */
export const queueSelectTargets = {
  head: 'queue.item.chk-9012',
  crossing: 'queue.item.chk-9014',
  next: 'queue.item.chk-9017',
  deadline: 'queue.deadline',
  capacity: 'queue.capacity',
  doomed: 'queue.group.doomed',
} as const;

/** The pool that every teaching state below describes. */
export const QUEUE_ARRIVALS_PER_SECOND = 5;
export const QUEUE_SERVICE_PER_SECOND = 2;
/** A 2 s client SLA minus the 100 ms handshake the client already spent. */
export const QUEUE_CLIENT_TIMEOUT_MS = 1900;

/** Eight checkouts, head first. Identity is stable across every state and every recompile. */
const teachingRoster: Array<Omit<QueueItem, 'waitedMs'>> = [
  { id: 'chk-9012', label: 'POST /checkout' },
  { id: 'chk-9014', label: 'GET /orders/9014' },
  { id: 'chk-9017', label: 'POST /payments' },
  { id: 'chk-9019', label: 'GET /cart/3312' },
  { id: 'chk-9021', label: 'POST /refunds' },
  { id: 'chk-9024', label: 'GET /inventory' },
  { id: 'chk-9026', label: 'GET /orders/9026' },
  { id: 'chk-9029', label: 'POST /carts/3320' },
];

export type QueueMoment = 'standing' | 'crossing' | 'compounding';
export const queueMoments = ['standing', 'crossing', 'compounding'] as const satisfies readonly QueueMoment[];

/**
 * Three moments 200 ms apart, sampled between two service completions, so the
 * same eight checkouts are present in all three: nothing is served and nothing
 * is admitted inside the window. Every wait grows by the same 200 ms, and one
 * more request crosses the client timeout at each step because the pool
 * admitted them 200 ms apart while it was still filling.
 */
export function queueViewMomentFixture(width = 640, moment: QueueMoment = 'standing'): QueueViewSpec {
  const elapsedMs = moment === 'standing' ? 0 : moment === 'crossing' ? 200 : 400;
  // Ages at the first moment: 200 ms arrival spacing from the burst, then the
  // 500 ms service spacing that has already replaced the two youngest slots.
  const baseAges = [2000, 1800, 1600, 1400, 1200, 1000, 400, 0];
  return {
    id: `queue-view-${moment}`,
    family: 'queue-view',
    view: 'plot',
    viewport: teachingViewport(width),
    capacity: 8,
    arrivalsPerSecond: QUEUE_ARRIVALS_PER_SECOND,
    servicePerSecond: QUEUE_SERVICE_PER_SECOND,
    clientTimeoutMs: QUEUE_CLIENT_TIMEOUT_MS,
    items: teachingRoster.map((entry, index) => ({ ...entry, waitedMs: baseAges[index]! + elapsedMs })),
    focus: moment === 'compounding' ? 'doomed' : 'head',
    provenance: `PostgreSQL checkout queue · eight waiters in front of a two-per-second pool, sampled ${elapsedMs} ms after the pool stood full`,
  };
}

/** Buffer sizes the learner may set. Below four the pool sheds so hard that no waiter is left to teach with. */
export const queueCapacityStops = [4, 5, 6, 7, 8] as const;
/** Pool sizes the learner may set. Five arrivals per second is the ceiling: at five the queue is no longer overloaded. */
export const queueServiceStops = [2, 3, 4] as const;

/**
 * The same pool once the queue has settled at a given buffer size. A standing
 * queue of `capacity` slots drained at `servicePerSecond` makes every waiter
 * wait its depth ÷ service rate, so the head has waited capacity ÷ rate.
 * Raising the buffer lengthens every wait; it never adds throughput.
 */
export function queueViewAtCapacity(width = 640, capacity = 8): QueueViewSpec {
  return settledQueue(width, capacity, QUEUE_SERVICE_PER_SECOND, `queue-view-capacity-${capacity}`,
    `PostgreSQL checkout queue · settled at a ${capacity}-slot buffer in front of a pool clearing ${QUEUE_SERVICE_PER_SECOND} checkouts per second`);
}

/** The same standing eight-slot queue served by a faster pool. */
export function queueViewAtServiceRate(width = 640, servicePerSecond = QUEUE_SERVICE_PER_SECOND): QueueViewSpec {
  return settledQueue(width, 8, servicePerSecond, `queue-view-service-${servicePerSecond}`,
    `PostgreSQL checkout queue · eight standing waiters in front of a pool clearing ${servicePerSecond} checkouts per second`);
}

function settledQueue(width: number, capacity: number, servicePerSecond: number, id: string, provenance: string): QueueViewSpec {
  return {
    id,
    family: 'queue-view',
    view: 'plot',
    viewport: teachingViewport(width),
    capacity,
    arrivalsPerSecond: QUEUE_ARRIVALS_PER_SECOND,
    servicePerSecond,
    clientTimeoutMs: QUEUE_CLIENT_TIMEOUT_MS,
    items: teachingRoster.slice(0, capacity).map((entry, index) => ({
      ...entry,
      waitedMs: Math.round(((capacity - index) / servicePerSecond) * 1000),
    })),
    focus: 'doomed',
    provenance,
  };
}

/** One viewport for every teaching state, so state pairs can compile motion. */
function teachingViewport(width: number): QueueViewSpec['viewport'] {
  return { width, height: width >= 620 ? 520 : 700 };
}
